/**
 * One variant option shown as tiles instead of a dropdown (Bernd, Freelo 32677799, 1. 10. 2026:
 * matrace v kompletu „podobně jako dřevo o řádek níže", vybraná se zvýrazněným podkladem).
 *
 * Like the material picker, this operates Horizon's <variant-picker> rather than replacing it:
 * a click sets the native <select> and lets a bubbling `change` out, so price, URL, buy
 * buttons and everything listening for variant:update keep working, and the native select
 * stays a working fallback when this script never runs.
 */

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function formatKc(cents) {
  return Math.round(cents / 100).toLocaleString('cs-CZ') + ' Kč';
}

/** The variant picker of this block's own product — a page also carries quick-add pickers. */
function ownPicker(root) {
  const scope = root.closest('.shopify-section') || document;
  return scope.querySelector('variant-picker') || document.querySelector('variant-picker');
}

function nativeSelect(root, name) {
  const picker = ownPicker(root);
  if (!picker) return null;
  for (const select of picker.querySelectorAll('select')) {
    if (select.name === `options[${name}]`) return select;
  }
  return null;
}

/**
 * Extra cost of each value against the cheapest one, with the other options held where they
 * are now — the same reading the wood buttons give ("+3 000 Kč", nothing on the cheapest).
 * Pure, so it can be checked in node against a real product.
 */
export function deltas(product, name, current) {
  const names = product.options.map((o) => (typeof o === 'string' ? o : o.name));
  const at = names.indexOf(name);
  if (at < 0) return new Map();
  const price = new Map();
  for (const v of product.variants) {
    const others = names.every((n, i) => i === at || current[n] == null || v.options[i] === current[n]);
    if (others) price.set(v.options[at], v.price);
  }
  if (!price.size) return new Map();
  const min = Math.min(...price.values());
  return new Map([...price].map(([value, p]) => [value, p - min]));
}

const productCache = new Map();
function loadProduct(handle) {
  if (!productCache.has(handle)) {
    productCache.set(handle, fetch(`/products/${handle}.js`).then((r) => r.json()).catch(() => null));
  }
  return productCache.get(handle);
}

async function render(root) {
  const name = root.dataset.optionName;
  const select = nativeSelect(root, name);
  const groups = root.querySelector('[data-option-tiles-group]');
  if (!select || !groups || select.options.length < 2) {
    root.hidden = true;
    return;
  }

  const current = {};
  for (const s of ownPicker(root).querySelectorAll('select')) {
    const m = /^options\[(.+)\]$/.exec(s.name);
    if (m) current[m[1]] = s.value;
  }
  const product = await loadProduct(root.dataset.productHandle);
  // /products/{handle}.js stops at 250 variants; past that a delta could be read off the
  // wrong variant, so show none rather than a wrong one.
  const extra = product && product.variants.length < 250 ? deltas(product, name, current) : new Map();

  const radio = `${root.id}-${name}`;
  // Hodnota bez varianty k volbám nad ní je v selectu jen jako hidden (variant-main-picker) — dlaždici nedostane.
  groups.innerHTML = [...select.options].filter((opt) => !opt.hidden).map((opt) => {
    const d = extra.get(opt.value);
    const price = d > 0 ? `+${formatKc(d)}` : '';
    return `
      <label class="option-tiles__tile${opt.disabled ? ' is-disabled' : ''}">
        <input type="radio" name="${escapeHtml(radio)}" value="${escapeHtml(opt.value)}"${opt.value === select.value ? ' checked' : ''}${opt.disabled ? ' disabled' : ''}>
        <span class="option-tiles__head">
          <span class="option-tiles__title">${escapeHtml(opt.value)}</span>
        </span>
        ${price ? `<span class="option-tiles__price">${price}</span>` : ''}
      </label>`;
  }).join('');

  hideNative(root, name);
  root.hidden = false;
}

/**
 * Hide the native dropdown with a rule in <head>, not an inline style: Horizon morphs the
 * picker after every variant change and an inline style on the old node does not survive
 * that. Scoped to this product's section so quick-add pickers elsewhere keep their select.
 * Only added once the tiles have drawn, so a page where the script fails keeps the select.
 */
function hideNative(root, name) {
  const section = root.closest('.shopify-section');
  if (!section || !section.id) return;
  const id = `option-tiles-hide-${section.id}-${name}`.replace(/[^\w-]/g, '_');
  if (document.getElementById(id)) return;
  const value = name.replace(/["\\]/g, '\\$&');
  const style = document.createElement('style');
  style.id = id;
  style.textContent = `#${CSS.escape(section.id)} variant-picker .variant-option:has(select[name="options[${value}]"]) { display: none; }`;
  document.head.appendChild(style);
}

function choose(root, value) {
  const select = nativeSelect(root, root.dataset.optionName);
  if (!select || select.value === value) return;
  select.value = value;
  if (select.value !== value) return;
  // Horizon reads the `selected` attribute when it builds the variant request.
  for (const option of select.options) option.toggleAttribute('selected', option.value === value);
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
}

function renderAll() {
  // Re-queried every pass: morph can replace the picker and this block after a variant change.
  for (const root of document.querySelectorAll('[data-option-tiles]')) {
    if (!root.dataset.bound) {
      root.dataset.bound = '1';
      root.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) choose(root, e.target.value);
      });
    }
    render(root).catch((e) => console.error('[option-tiles]', e));
  }
}

if (typeof document !== 'undefined') {
  renderAll();
  // Immediately, not via requestAnimationFrame: a hidden tab never runs one, and the page is
  // already morphed when variant:update fires (the Prodloužení line reads the new price here).
  document.addEventListener('variant:update', renderAll);
}
